import { useBuilderContext } from "@Context/game/builder/builder-context";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite } from "@pixi/react";
import Dot2 from "@Resources/game/builder/dot2.png";
import Dot3 from "@Resources/game/builder/dot3.png";
import Dot4 from "@Resources/game/builder/dot4.png";
import MouseEvents from "@Enums/MouseEvents";
import { Point } from "@Shared/types/ScenarioTypes";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { getSelectedPlayer } from "./functions/player-functions";
import { getClampedPosition, isValidIndex, stageToScreen } from "./functions/utilities";
import { Waypoint as WaypointType } from "./models/builder-data-types";

const Waypoint = (props: {
  index: number;
  position: { x: number; y: number };
  prev: { x: number; y: number };
  next: { x: number; y: number };
  time: number;
  color?: string;
  selected?: boolean;
  routeSelected?: boolean;
  size?: number;
  hasNext?: boolean;
  onDragStart?: () => void;
  onChange?: (point: WaypointType) => void;
}) => {
  const size = props.size || 24;
  const tangSize = size * 0.75;

  const { state, dispatch } = useBuilderContext();
  const [selected, setSelected] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [routeSelected, setRouteSelected] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [position, setPosition] = useState({ x: props.position.x, y: props.position.y });
  const [previous, setPrevious] = useState({ x: props.prev.x, y: props.prev.y });
  const [next, setNext] = useState({ x: props.next.x, y: props.next.y });
  const positionRef = useRef(position);
  const previousRef = useRef(previous);
  const nextRef = useRef(next);
  const dragStartPositionRef = useRef({ x: 0, y: 0 });
  const activeDragRef = useRef<"position" | "previous" | "next" | null>(null);
  const dragTargetRef = useRef<any>(null);

  useEffect(() => {
    const newPosition = { x: props.position.x, y: props.position.y };
    positionRef.current = newPosition;
    setPosition(newPosition);
  }, [props.position]);

  useEffect(() => {
    const newPrevious = { x: props.prev.x, y: props.prev.y };
    previousRef.current = newPrevious;
    setPrevious(newPrevious);
  }, [props.prev]);

  useEffect(() => {
    const newNext = { x: props.next.x, y: props.next.y };
    nextRef.current = newNext;
    setNext(newNext);
  }, [props.next]);

  const onClick = (event: any) => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer) return;

    if (!hasDragged && selectedPlayer && isValidIndex(props.index, selectedPlayer.points)) {
      if (state.selectedWaypoint === props.index) {
        dispatch({
          selectedAction: -1,
          showWaypointRadial: true,
          showPlayerRadial: false,
          showFieldRadial: false,
          showActionRadial: false,
        });
      }

      const waypointPos = selectedPlayer.points[props.index].position;
      dispatch({
        selectedWaypoint: props.index,
        selectedAction: -1,
        selectedPosition: waypointPos,
        radialAnchor: getClampedPosition(stageToScreen(waypointPos, state), state.radialSize, state.field.mapWidth, state.field.mapHeight),
        showPlayerRadial: false,
        showFieldRadial: false,
        showActionRadial: false,
      });
    }
    setHasDragged(false);
  };

  const endActiveDrag = useCallback(() => {
    const dragType = activeDragRef.current;
    if (!dragType) return;

    const sprite = dragTargetRef.current;
    if (sprite) {
      sprite.alpha = 1;
      sprite.dragging = false;
      sprite.data = null;
    }

    if (props.onChange) {
      if (dragType === "position") {
        const relativePosition = {
          x: positionRef.current.x - dragStartPositionRef.current.x,
          y: positionRef.current.y - dragStartPositionRef.current.y,
        };
        props.onChange({
          position: positionRef.current,
          prev: { x: previousRef.current.x + relativePosition.x, y: previousRef.current.y + relativePosition.y },
          next: { x: nextRef.current.x + relativePosition.x, y: nextRef.current.y + relativePosition.y },
          t: props.time,
        });
      } else {
        props.onChange({ position: positionRef.current, prev: previousRef.current, next: nextRef.current, t: props.time });
      }
    }

    activeDragRef.current = null;
    dragTargetRef.current = null;
  }, [props.onChange, props.time]);

  const onDragStart = (event: any, dragType: "position" | "previous" | "next") => {
    const sprite = event.currentTarget;
    sprite.alpha = 0.5;
    sprite.data = event.data;
    sprite.dragging = true;
    dragTargetRef.current = sprite;
    activeDragRef.current = dragType;
    dragStartPositionRef.current = { x: sprite.x, y: sprite.y };
    setHasDragged(false);
    if (props.onDragStart) props.onDragStart();
  };

  useEffect(() => {
    window.addEventListener(MouseEvents.POINTER_UP, endActiveDrag);
    window.addEventListener(MouseEvents.BLUR, endActiveDrag);
    return () => {
      window.removeEventListener(MouseEvents.POINTER_UP, endActiveDrag);
      window.removeEventListener(MouseEvents.BLUR, endActiveDrag);
    };
  }, [endActiveDrag]);

  const onDragMove = (event: any, mutator: (p: Point) => void, ref: MutableRefObject<Point>) => {
    const sprite = event.currentTarget;
    if (sprite.dragging) {
      const newPosition = sprite.data.getLocalPosition(sprite.parent);
      sprite.x = newPosition.x;
      sprite.y = newPosition.y;
      const point = { x: sprite.x, y: sprite.y };
      ref.current = point;
      mutator(point);
      setHasDragged(true);
    }
  };

  useEffect(() => {
    setRouteSelected(props.routeSelected || false);
  }, [props.routeSelected]);

  useEffect(() => {
    setSelected(props.selected || false);
  }, [props.selected]);

  useEffect(() => {
    if (selected)
      dispatch({
        selectedAction: -1,
      });
  }, [selected]);

  const draw = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      if (props.selected) {
        g.lineStyle(3 * state.spriteScale, 0xffffff, 0.5);
        if (props.index > 0) {
          g.moveTo(props.position.x, props.position.y);
          g.lineTo(props.prev.x, props.prev.y);
        }
        if (props.hasNext) {
          g.moveTo(props.position.x, props.position.y);
          g.lineTo(props.next.x, props.next.y);
        }
      }
    },
    [props, selected, state.spriteScale]
  );

  return (
    <Container>
      <Graphics draw={draw} />
      <Sprite
        image={props.selected ? Dot4 : Dot2}
        width={size * state.spriteScale}
        height={size * state.spriteScale}
        tint={selected ? "white" : props.color || "white"}
        alpha={routeSelected ? (hovered ? 0.85 : 1) : 0.2}
        x={position.x}
        y={position.y}
        anchor={0.5}
        eventMode={"static"}
        pointerdown={(e) => onDragStart(e, "position")}
        onglobalpointermove={(e) => onDragMove(e, setPosition, positionRef)}
        pointerup={endActiveDrag}
        pointerupoutside={endActiveDrag}
        pointertap={onClick}
        mouseover={() => {
          setHovered(true);
        }}
        mouseout={() => {
          setHovered(false);
        }}
      />
      {props.selected && props.index > 0 && (
        <Sprite
          image={Dot3}
          width={tangSize * state.spriteScale}
          height={tangSize * state.spriteScale}
          tint={selected ? "white" : props.color || "white"}
          alpha={hovered ? 0.85 : 1}
          x={previous.x}
          y={previous.y}
          anchor={0.5}
          eventMode={"dynamic"}
          pointerdown={(e) => onDragStart(e, "previous")}
          onglobalpointermove={(e) => onDragMove(e, setPrevious, previousRef)}
          pointerup={endActiveDrag}
          pointerupoutside={endActiveDrag}
          mouseover={() => {
            setHovered(true);
          }}
          mouseout={() => {
            setHovered(false);
          }}
        />
      )}
      {props.selected && props.hasNext && (
        <Sprite
          image={Dot3}
          width={tangSize * state.spriteScale}
          height={tangSize * state.spriteScale}
          tint={selected ? "white" : props.color || "white"}
          alpha={hovered ? 0.85 : 1}
          x={next.x}
          y={next.y}
          anchor={0.5}
          eventMode={"dynamic"}
          pointerdown={(e) => onDragStart(e, "next")}
          onglobalpointermove={(e) => onDragMove(e, setNext, nextRef)}
          pointerup={endActiveDrag}
          pointerupoutside={endActiveDrag}
          mouseover={() => {
            setHovered(true);
          }}
          mouseout={() => {
            setHovered(false);
          }}
        />
      )}
    </Container>
  );
};

export default Waypoint;
